import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StatementGeneratorService } from '../../services/statement-generator.service';
import { StatementGenerationDetails, GeneratedStatement, StatementInputSource } from '../../models/statement.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-statement-generation-details',
    templateUrl: './statement-generation-details.component.html',
    styleUrls: ['./statement-generation-details.component.scss'],
    standalone: false
})
export class StatementGenerationDetailsComponent implements OnInit {

  projectId: number;
  processId: string;

  generationDetails: StatementGenerationDetails | null = null;
  loading = false;
  error: string | null = null;

  // View mode: 'all' or 'by-section'
  viewMode: 'all' | 'by-section' = 'all';

  // Grouped statements by section
  statementsBySection: { [section: string]: GeneratedStatement[] } = {};
  sections: string[] = [];

  // Input sources grouped by type
  inputSourcesByType: {
    surveys: StatementInputSource[];
    pdfs: StatementInputSource[];
    pubmed: StatementInputSource[];
    grades: StatementInputSource[];
    other: StatementInputSource[];
  } = {
    surveys: [],
    pdfs: [],
    pubmed: [],
    grades: [],
    other: []
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private statementService: StatementGeneratorService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.route.parent?.params.subscribe(parentParams => {
      console.log('Parent route params:', parentParams);
      this.projectId = +parentParams['project_id'];
      console.log('Extracted projectId from parent:', this.projectId);
    });

    this.route.params.subscribe(params => {
      console.log('Route params:', params);
      this.processId = params['processId'];
      console.log('Extracted processId:', this.processId);

      if (this.projectId && this.processId) {
        console.log('Loading generation details...');
        this.loadGenerationDetails();
      } else {
        console.error('Missing projectId or processId!', { projectId: this.projectId, processId: this.processId });
        this.error = 'Missing project ID or process ID in route parameters';
        this.loading = false;
      }
    });
  }

  /**
   * Load generation details including statements and input sources
   */
  loadGenerationDetails(): void {
    this.loading = true;
    this.error = null;

    console.log('Loading generation details for processId:', this.processId);

    this.statementService.getGenerationDetails(
      this.processId,
      (details: StatementGenerationDetails) => {
        console.log('Generation details loaded:', details);
        this.generationDetails = details;
        this.loading = false;

        // Group input sources by type
        this.groupInputSourcesByType(details.input_sources || []);

        // Group statements by section
        this.groupStatementsBySection(details.statements || []);
      },
      (error) => {
        console.error('Error loading generation details:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);

        // Fallback: Try to load using existing endpoints
        if (error?.status === 404) {
          console.log('Details endpoint not found, trying fallback method...');
          this.loadGenerationDetailsFallback();
        } else if (error?.status === 500) {
          this.error = 'Server error. Please check backend logs.';
          this.loading = false;
          this.showMessage(this.error, 'error');
        } else {
          this.error = 'Error loading generation details: ' + (error?.message || 'Unknown error');
          this.loading = false;
          this.showMessage(this.error, 'error');
        }
      }
    );
  }

  /**
   * Fallback method using existing endpoints
   */
  private loadGenerationDetailsFallback(): void {
    console.log('Using fallback method to load generation details');

    // Load process status
    this.statementService.getProcessStatus(
      this.processId,
      (process: any) => {
        console.log('Process status loaded:', process);

        // Load statements
        this.statementService.getGeneratedStatements(
          this.processId,
          (statements: any) => {
            console.log('Statements loaded:', statements);

            // Construct details object from available data
            this.generationDetails = {
              id: process.process_id || this.processId,
              name: process.name || `Generation ${this.processId}`,
              generation_number: process.generation_number,
              project_id: this.projectId,
              organization_id: process.organization_id,
              status: process.status,
              progress_percentage: process.progress_percentage || 100,
              message: process.message,
              error_message: process.error_message,
              created_at: process.created_at,
              updated_at: process.updated_at,
              completed_at: process.completed_at,
              input_sources: process.input_sources || [],
              input_sources_count: process.input_sources_count || 0,
              statements: statements || [],
              generated_statements_count: statements?.length || 0
            };

            this.loading = false;

            // Group input sources by type
            this.groupInputSourcesByType(this.generationDetails.input_sources);

            // Group statements by section
            this.groupStatementsBySection(this.generationDetails.statements);

            this.showMessage('Loaded generation details using fallback method', 'warning');
          },
          (statementsError) => {
            console.error('Error loading statements in fallback:', statementsError);
            this.error = 'Error loading statements: ' + (statementsError?.message || 'Unknown error');
            this.loading = false;
            this.showMessage(this.error, 'error');
          }
        );
      },
      (statusError) => {
        console.error('Error loading process status in fallback:', statusError);
        this.error = 'Error loading process status: ' + (statusError?.message || 'Unknown error');
        this.loading = false;
        this.showMessage(this.error, 'error');
      }
    );
  }

  /**
   * Group input sources by type
   */
  private groupInputSourcesByType(sources: StatementInputSource[]): void {
    this.inputSourcesByType = {
      surveys: [],
      pdfs: [],
      pubmed: [],
      grades: [],
      other: []
    };

    sources.forEach(source => {
      switch (source.type) {
        case 'survey':
          this.inputSourcesByType.surveys.push(source);
          break;
        case 'pdf':
          this.inputSourcesByType.pdfs.push(source);
          break;
        case 'pubmed':
          this.inputSourcesByType.pubmed.push(source);
          break;
        case 'grade':
          this.inputSourcesByType.grades.push(source);
          break;
        default:
          this.inputSourcesByType.other.push(source);
      }
    });
  }

  /**
   * Group statements by section
   */
  private groupStatementsBySection(statements: GeneratedStatement[]): void {
    this.statementsBySection = {};

    statements.forEach(statement => {
      const section = statement.section || 'Uncategorized';
      if (!this.statementsBySection[section]) {
        this.statementsBySection[section] = [];
      }
      this.statementsBySection[section].push(statement);
    });

    this.sections = Object.keys(this.statementsBySection).sort();
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: string): string {
    const classes = {
      'completed': 'badge-success',
      'processing': 'badge-warning',
      'failed': 'badge-danger',
      'pending': 'badge-secondary'
    };
    return classes[status] || 'badge-secondary';
  }

  /**
   * Get status icon
   */
  getStatusIcon(status: string): string {
    const icons = {
      'completed': 'check_circle',
      'processing': 'hourglass_empty',
      'failed': 'error',
      'pending': 'schedule'
    };
    return icons[status] || 'help';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labels = {
      'completed': 'Completato',
      'processing': 'In elaborazione',
      'failed': 'Fallito',
      'pending': 'In attesa'
    };
    return labels[status] || status;
  }

  /**
   * Get input source icon
   */
  getInputSourceIcon(type: string): string {
    const icons = {
      'survey': 'poll',
      'pdf': 'picture_as_pdf',
      'pubmed': 'science',
      'grade': 'grading',
      'other': 'description'
    };
    return icons[type] || 'description';
  }

  /**
   * Get input source type label
   */
  getInputSourceTypeLabel(type: string): string {
    const labels = {
      'survey': 'Survey',
      'pdf': 'PDF Document',
      'pubmed': 'PubMed Article',
      'grade': 'GRADE Revision',
      'other': 'Other'
    };
    return labels[type] || type;
  }

  /**
   * Toggle view mode
   */
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'all' ? 'by-section' : 'all';
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    this.router.navigate(['/projects', this.projectId]);
  }

  /**
   * Export statements to CSV or JSON
   */
  exportStatements(format: 'csv' | 'json'): void {
    if (!this.generationDetails || !this.generationDetails.statements) {
      return;
    }

    const statements = this.generationDetails.statements;

    if (format === 'json') {
      this.exportAsJSON(statements);
    } else if (format === 'csv') {
      this.exportAsCSV(statements);
    }
  }

  /**
   * Export as JSON
   */
  private exportAsJSON(statements: GeneratedStatement[]): void {
    const dataStr = JSON.stringify(statements, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportName = `${this.generationDetails?.name}_statements.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();

    this.showMessage('Statements exported as JSON', 'success');
  }

  /**
   * Export as CSV
   */
  private exportAsCSV(statements: GeneratedStatement[]): void {
    const headers = ['ID', 'Statement', 'Comment', 'Section', 'Level of Evidence', 'Grade', 'Confidence Score'];
    const rows = statements.map(s => [
      s.id || '',
      `"${(s.statement_text || '').replace(/"/g, '""')}"`,
      `"${(s.comment || '').replace(/"/g, '""')}"`,
      s.section || '',
      s.level_of_evidence || '',
      s.grade_of_recommendation || '',
      s.confidence_score || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);

    const exportName = `${this.generationDetails?.name}_statements.csv`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();

    this.showMessage('Statements exported as CSV', 'success');
  }

  /**
   * Show message
   */
  private showMessage(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
